/**
 * Risico Meld Dialog — WbModal voor field-medewerker om een risico op een
 * Wasserij_item te melden tijdens voorinspectie.
 *
 * Flow: vink aan wat je ziet, eventueel een toelichting, submit. Daarna rond
 * je de voorinspectie gewoon af; het kleed blijft liggen tot kantoor de
 * melding beoordeeld heeft.
 *
 * De toelichting is sinds het versimpelde proces optioneel: de types zijn het
 * signaal, en de mail naar de klant wordt door kantoor geschreven. Zo kost een
 * melding hooguit een paar tellen en blijft niemand hangen op een tekstveld.
 *
 * Server-side wordt RisicoMeldingService.meldRisico aangeroepen met
 * bron='Field-App' (KPI "% risico-flows autonoom").
 *
 * Foto's via de eigen camera (fase 'risico'), gekoppeld aan het item.
 */

import { useState } from 'react';
import { WbButton, WbFlag, WbModal, WbTextarea, WbTile } from './wb';
import { salesforceClient } from '../lib/salesforce';
import { RISICO_TYPE_OPTIONS } from '../lib/constants';
import { CameraCapture } from './CameraCapture';

const TOELICHTING_MAX_LENGTH = 1000;

interface Props {
  itemId: string;
  itemName: string;
  /** Niet meer gebruikt (CompanyCam-tak weg); blijft in de props voor de aanroepers. */
  companyCamProjectId: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}

export function RisicoMeldDialog({
  itemId,
  itemName,
  onClose,
  onSubmitted,
}: Props) {
  const [types, setTypes] = useState<string[]>([]);
  const [toelichting, setToelichting] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [fotoCount, setFotoCount] = useState(0);

  const toggleType = (value: string) => {
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  // Zodra er iets staat (types, tekst of foto's) sluit een tik op de
  // achtergrond de dialog niet meer: op een telefoon is die mis-tik zó
  // gemaakt, en de melding verdween dan geruisloos zonder dat er ooit iets
  // naar de server ging (WI-332384, 10-08). Sluiten kan dan alleen nog
  // expliciet via Annuleren of het kruisje.
  const isDirty = types.length > 0 || toelichting.trim().length > 0 || fotoCount > 0;

  // Alleen de types zijn verplicht; toelichting is welkom maar geen drempel.
  const isValid = types.length > 0 && toelichting.length <= TOELICHTING_MAX_LENGTH;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await salesforceClient.meldRisico(itemId, {
        risicoTypes: types,
        toelichting: toelichting.trim(),
      });
      if (!res.success) {
        setError(res.message || 'Risico melden mislukt');
        return;
      }
      if (navigator.vibrate) navigator.vibrate(50);
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <WbModal
        open
        onClose={onClose}
        variant="sheet"
        titel="Risico melden"
        subtitel={itemName}
        sluitOpAchtergrond={!isDirty}
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={onClose} disabled={submitting}>Annuleren</WbButton>
            <WbButton variant="danger" size="lg" onClick={handleSubmit} disabled={!isValid} isLoading={submitting}>Risico melden</WbButton>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-wb-inkt2">
            Vink aan wat je ziet en rond de voorinspectie daarna gewoon af. Het kleed blijft liggen tot kantoor je melding heeft beoordeeld.
          </p>

          <section className="space-y-2">
            <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Foto's</h3>
            <WbButton variant="secondary" onClick={() => setShowCamera(true)}>
              {fotoCount > 0 ? `Maak foto's (${fotoCount} gemaakt)` : "Maak foto's"}
            </WbButton>
            <p className="text-sm text-wb-inkt2">Foto's worden gekoppeld en in de mail naar de klant meegestuurd.</p>
          </section>

          <section className="space-y-2">
            <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Wat zie je? (één of meer)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {RISICO_TYPE_OPTIONS.map((value) => (
                <WbTile key={value} selected={types.includes(value)} onToggle={() => toggleType(value)}>{value}</WbTile>
              ))}
            </div>
          </section>

          <WbTextarea
            label="Toelichting (optioneel)"
            value={toelichting}
            onChange={(e) => setToelichting(e.target.value)}
            placeholder="Iets wat kantoor moet weten? Schrijf het hier."
            rows={4}
            maxLength={TOELICHTING_MAX_LENGTH}
          />
          <p className="font-mono text-sm text-wb-inkt2 -mt-2">
            {toelichting.length > 0 ? `${toelichting.length}/${TOELICHTING_MAX_LENGTH}` : 'Kantoor schrijft de mail naar de klant'}
          </p>

          {error && <WbFlag kind="geld">{error}</WbFlag>}
        </div>
      </WbModal>

      {showCamera && (
        <CameraCapture
          fase="risico"
          doel={{ wiSfId: itemId }}
          subtitle={itemName}
          onClose={(aantal) => {
            setShowCamera(false);
            if (aantal > 0) setFotoCount((prev) => prev + aantal);
          }}
        />
      )}
    </>
  );
}
